import { FC, useLayoutEffect, useRef, useState } from "react";
import DbIcon from "designs/DesignB/components/icons";
import { DetailSpec } from "./carDetails";

/** Spec cells: icon, value, label. Only the ones with a value come in. */
export const SpecGrid: FC<{ specs: DetailSpec[]; className?: string }> = ({ specs, className = "" }) => (
  <ul className={`db-xp-specs ${className}`} aria-label="Specs">
    {specs.map((s) => (
      <li key={s.key} className="db-xp-spec">
        <DbIcon name={s.icon} className="db-xp-spec__icon" />
        <b>{s.value}</b>
        <span>{s.label}</span>
      </li>
    ))}
  </ul>
);

/** A feature chip. */
export const Feature: FC<{ name: string }> = ({ name }) => (
  <li className="db-xp-feat">
    <DbIcon name="tick" className="db-xp-feat__icon" />
    <span>{name}</span>
  </li>
);

/** The description, three lines and a Read more that opens the rest in place when it runs longer. */
const Description: FC<{ text: string }> = ({ text }) => {
  const p = useRef<HTMLParagraphElement>(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = p.current;
    if (el && !open) setLong(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);
  return (
    <section>
      <h4 className="db-xp-h">About this car</h4>
      <p ref={p} className={`db-xp-desc ${open ? "" : "is-clamped"}`}>
        {text}
      </p>
      {(long || open) && (
        <button type="button" className="db-xp-link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </section>
  );
};

export const FEATURES_SHOWN = 6;

/** Features as chips: six, then a "+N more" that shows the rest in place. */
const Features: FC<{ names: string[] }> = ({ names }) => {
  const [all, setAll] = useState(false);
  const hidden = names.length - FEATURES_SHOWN;
  return (
    <section className="db-xp-fsec">
      <h4 className="db-xp-h">
        Features<small>{names.length}</small>
      </h4>
      <ul className="db-xp-feats">
        {(all ? names : names.slice(0, FEATURES_SHOWN)).map((f) => (
          <Feature key={f} name={f} />
        ))}
        {hidden > 0 && (
          <li>
            <button type="button" className="db-xp-more" aria-expanded={all} onClick={() => setAll((v) => !v)}>
              {all ? "Show less" : `+${hidden} more`}
            </button>
          </li>
        )}
      </ul>
    </section>
  );
};

export const EMPTY_DETAILS = "The rental company hasn't added details for this car yet.";

/**
 * The car's specs, description and features (1NOW-11219), one renderer for the inline panel and,
 * later, the car page. Features run the full width under the panel's two columns, so the body is
 * split in two: `part="main"` (specs and description) and `part="features"`.
 */
const CarDetailsBody: FC<{
  specs: DetailSpec[];
  description: string | null;
  features: string[];
  part?: "main" | "features" | "all";
}> = ({ specs, description, features, part = "all" }) => {
  const main = part !== "features";
  const feats = part !== "main";
  return (
    <>
      {main && specs.length > 0 && <SpecGrid specs={specs} />}
      {main && description && <Description text={description} />}
      {main && !specs.length && !description && !features.length && <p className="db-xp-empty">{EMPTY_DETAILS}</p>}
      {feats && features.length > 0 && <Features names={features} />}
    </>
  );
};

export default CarDetailsBody;
